import React, { useMemo } from 'react';
import { DownloadIcon, MailIcon } from 'lucide-react';
import { policyMeta, policySections } from '../data/privacyPolicy';
import { PolicyContents } from '../components/legal/PolicyContents';
import { PolicySectionBody } from '../components/legal/PolicySectionBody';
import { BackToTop } from '../components/legal/BackToTop';
import { SiteFooter } from '../components/ui/SiteFooter';
import { SiteHeader } from '../components/ui/SiteHeader';
import { useActiveSection } from '../hooks/useActiveSection';
const metaLinkStyles = 'inline-flex items-center gap-2 text-sm font-medium text-purple-700 underline underline-offset-4 decoration-purple-300 hover:text-purple-900 hover:decoration-purple-700 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory rounded';
export function PrivacyPolicyPage() {
  const sectionIds = useMemo(() => policySections.map(section => section.id), []);
  const activeId = useActiveSection(sectionIds);
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-857" data-mp="24ss9oclspti0">
      {/* Navigation */}
      <SiteHeader currentPath="/privacy-policy" data-id="element-858" data-mp="2jpaoe7t3uvlv" />

      <main className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-859" data-mp="2eqzfbzamwp4p">
        {/* Title area */}
        <header className="pt-16 md:pt-24 pb-10 md:pb-14 border-b border-charcoal/10" data-id="element-860" data-mp="2eov9vcx37i2b">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-5" data-id="element-861" data-mp="1qz139d3d2pqi">
            {policyMeta.company}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-charcoal leading-[1.05] tracking-tight" data-id="element-862" data-mp="20r6pe66xmcxs">
            {policyMeta.title}
          </h1>
          <p className="mt-6 max-w-[62ch] text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-863" data-mp="1vy5a3x2tcbdo">
            {policyMeta.summary}
          </p>

          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-id="element-864" data-mp="13ccf49nre11g">
            <div className="flex items-baseline gap-2" data-id="element-865" data-mp="1szy97gh1gawg">
              <dt className="text-charcoal/50" data-id="element-866" data-mp="1szg7w5fir6ho">Version</dt>
              <dd className="font-medium text-charcoal" data-id="element-867" data-mp="1x0i74m9frijn">
                {policyMeta.version.replace('Version ', '')}
              </dd>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-868" data-mp="21ucovk5hdng3">
              <dt className="text-charcoal/50" data-id="element-869" data-mp="26q1v26c0a2ld">Effective</dt>
              <dd className="font-medium text-charcoal" data-id="element-870" data-mp="1a13wed14xkix">September 2026</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-871" data-mp="1e26139dtmosa">
            <a href={policyMeta.pdfUrl} target="_blank" rel="noopener noreferrer" className={metaLinkStyles} data-id="element-872" data-mp="1e48w67ttz83v">
              <DownloadIcon aria-hidden="true" className="w-4 h-4" data-id="element-873" data-mp="1o1wqiwniydli" />
              Download the PDF
            </a>
            <a href={`mailto:${policyMeta.email}`} className={metaLinkStyles} data-id="element-874" data-mp="2uj1rxoq3k2uh">
              <MailIcon aria-hidden="true" className="w-4 h-4" data-id="element-875" data-mp="2zdxnuzygfacv" />
              {policyMeta.email}
            </a>
          </div>
        </header>

        {/* Index + body */}
        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14 xl:gap-20 pb-24 md:pb-32" data-id="element-876" data-mp="iun7ginr2ae7">
          <PolicyContents sections={policySections} activeId={activeId} data-id="element-877" data-mp="e1atr3klzo08" />

          <article className="max-w-[68ch] pt-10 lg:pt-14 divide-y divide-charcoal/10" data-id="element-878" data-mp="94aqf0f58zct">
            {policySections.map(section => <PolicySectionBody key={section.id} section={section} data-id="element-879" data-mp="538ogbtue975" />)}

            <p className="pt-12 text-sm text-charcoal/50" data-id="element-880" data-mp="21hug3frg22">
              © {new Date().getFullYear()} Fem Entity Inc. All rights reserved.{' '}
              {policyMeta.version} — {policyMeta.effectiveDate}.
            </p>
          </article>
        </div>
      </main>

      <BackToTop data-id="element-881" data-mp="1piajpsjhhqr3" />

      <SiteFooter currentPath="/privacy-policy" data-id="element-882" data-mp="1pgpoe0pci3s6" />
    </div>;
}